/** Match Deepgram Playground live settings (Smart Format + Punctuation, no interim/endpointing). */
export const DEEPGRAM_PLAYGROUND_COMPAT_MODE = true;

/** Optional PCM streaming fallback — off unless MediaRecorder mode fails. */
export const USE_PCM_STREAMING = false;

export const MEDIA_RECORDER_TIMESLICE_MS = 250;

export const CLOSE_STREAM_WAIT_MS = 1000;

export const RECENT_SEGMENT_DEDUPE_SIZE = 5;

export interface DeepgramListenConfig {
  model: string;
  language: string;
  smart_format: boolean;
  punctuate: boolean;
  interim_results: boolean;
  encoding?: string;
  sample_rate?: number;
  channels?: number;
}

const DEEPGRAM_LISTEN_BASE = "wss://api.deepgram.com/v1/listen";

/** Build Playground-compatible Deepgram listen query config for a selected language. */
export function buildDeepgramListenConfig(
  languageCode: string,
  options?: { pcm?: boolean; sampleRate?: number }
): DeepgramListenConfig {
  const config: DeepgramListenConfig = {
    model: "nova-3",
    language: languageCode,
    smart_format: true,
    punctuate: true,
    interim_results: DEEPGRAM_PLAYGROUND_COMPAT_MODE ? false : true,
  };

  if (options?.pcm && USE_PCM_STREAMING) {
    config.encoding = "linear16";
    config.sample_rate = options.sampleRate ?? 48000;
    config.channels = 1;
  }

  return config;
}

/** Build WebSocket listen URL from config (no secrets). */
export function buildDeepgramListenUrl(
  languageCode: string,
  options?: { pcm?: boolean; sampleRate?: number }
): string {
  const config = buildDeepgramListenConfig(languageCode, options);
  const params = new URLSearchParams({
    model: config.model,
    language: config.language,
    smart_format: String(config.smart_format),
    punctuate: String(config.punctuate),
    interim_results: String(config.interim_results),
  });

  if (config.encoding) params.set("encoding", config.encoding);
  if (config.sample_rate) params.set("sample_rate", String(config.sample_rate));
  if (config.channels) params.set("channels", String(config.channels));

  return `${DEEPGRAM_LISTEN_BASE}?${params.toString()}`;
}

/** Sanitize ws URL for debug display (strip any token query params). */
export function sanitizeDeepgramWsUrl(wsUrl: string): string {
  try {
    const url = new URL(wsUrl);
    url.searchParams.delete("token");
    return url.toString();
  } catch {
    return wsUrl.replace(/token=[^&]+/gi, "token=[REDACTED]");
  }
}

export function wsReadyStateLabel(state: number | undefined): string {
  switch (state) {
    case WebSocket.CONNECTING:
      return "CONNECTING";
    case WebSocket.OPEN:
      return "OPEN";
    case WebSocket.CLOSING:
      return "CLOSING";
    case WebSocket.CLOSED:
      return "CLOSED";
    default:
      return "NONE";
  }
}
